// Transition Utilities for Enhanced User Experience

export interface TransitionConfig {
  duration?: number
  delay?: number
  easing?: string
  stagger?: boolean
  staggerDelay?: number
}

export interface ScrollRevealConfig {
  threshold?: number
  rootMargin?: string
  triggerOnce?: boolean
}

// Predefined transition configurations
export const TRANSITION_PRESETS = {
  fast: {
    duration: 150,
    easing: 'ease-in-out'
  },
  normal: {
    duration: 300,
    easing: 'ease-in-out'
  },
  slow: {
    duration: 500,
    easing: 'ease-in-out'
  },
  bounce: {
    duration: 400,
    easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },
  smooth: {
    duration: 350,
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
  },
  elastic: {
    duration: 600,
    easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  }
} as const

// CSS class utilities
export const TRANSITION_CLASSES = {
  // Page transitions
  pageEnter: 'page-enter',
  pageEnterActive: 'page-enter-active',
  pageExit: 'page-exit',
  pageExitActive: 'page-exit-active',

  // Component transitions
  fadeIn: 'component-fade-in',
  slideInLeft: 'component-slide-in-left',
  slideInRight: 'component-slide-in-right',
  scaleIn: 'component-scale-in',
  bounceIn: 'component-bounce-in',

  // Enhanced elements
  enhancedButton: 'btn-enhanced',
  enhancedCard: 'card-enhanced',
  enhancedFormInput: 'form-input-enhanced',
  enhancedTableRow: 'table-row-enhanced',
  enhancedModal: 'modal-enhanced',

  // Hover effects
  hoverLift: 'hover-lift',
  hoverGlow: 'hover-glow',
  hoverScale: 'hover-scale',
  hoverRotate: 'hover-rotate',

  // Interactive elements
  interactive: 'interactive-enhanced',
  focusRing: 'focus-ring',

  // Loading states
  loadingShimmer: 'loading-shimmer',
  loadingPulse: 'loading-pulse',
  loadingBounce: 'loading-bounce',

  // Stagger animations
  staggerContainer: 'stagger-container',
  staggerItem: 'stagger-item',

  // Scroll reveal
  scrollReveal: 'scroll-reveal',

  // Performance optimizations
  gpuAccelerated: 'gpu-accelerated',
  willChangeTransform: 'will-change-transform',
  willChangeOpacity: 'will-change-opacity'
} as const

// Utility functions for creating smooth transitions
export const TransitionUtils = {
  /**
   * Apply transition classes to an element
   */
  applyTransitionClasses(element: HTMLElement, classes: string[]): void {
    element.classList.add(...classes)
  },

  /**
   * Remove transition classes from an element
   */
  removeTransitionClasses(element: HTMLElement, classes: string[]): void {
    element.classList.remove(...classes)
  },

  /**
   * Apply staggered animations to child elements
   */
  applyStaggerAnimation(container: HTMLElement, childSelector = '.stagger-item'): void {
    container.classList.add(TRANSITION_CLASSES.staggerContainer)

    const children = container.querySelectorAll(childSelector)
    children.forEach((child, index) => {
      (child as HTMLElement).classList.add(TRANSITION_CLASSES.staggerItem)
      ;(child as HTMLElement).style.animationDelay = `${index * 0.1}s`
    })
  },

  /**
   * Create a smooth scroll reveal effect
   */
  createScrollReveal(
    elements: NodeListOf<Element> | Element[],
    config: ScrollRevealConfig = {}
  ): IntersectionObserver {
    const {
      threshold = 0.1,
      rootMargin = '0px 0px -50px 0px',
      triggerOnce = true
    } = config

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed')
            if (triggerOnce) {
              observer.unobserve(entry.target)
            }
          } else if (!triggerOnce) {
            entry.target.classList.remove('revealed')
          }
        })
      },
      { threshold, rootMargin }
    )

    elements.forEach((element) => {
      element.classList.add(TRANSITION_CLASSES.scrollReveal)
      observer.observe(element)
    })

    return observer
  },

  /**
   * Create a smooth page transition
   */
  async createPageTransition(
    element: HTMLElement,
    direction: 'enter' | 'exit' = 'enter',
    duration = 300
  ): Promise<void> {
    await new Promise<void>((resolve) => {
      const enterClass = direction === 'enter' ? TRANSITION_CLASSES.pageEnter : TRANSITION_CLASSES.pageExit
      const activeClass = direction === 'enter'
        ? TRANSITION_CLASSES.pageEnterActive
        : TRANSITION_CLASSES.pageExitActive

      element.classList.add(enterClass)

      // Force reflow
      void element.offsetHeight

      element.classList.add(activeClass)
      element.classList.remove(enterClass)

      setTimeout(() => {
        element.classList.remove(activeClass)
        resolve()
      }, duration)
    })
  },

  /**
   * Create a loading skeleton effect
   */
  createLoadingSkeleton(element: HTMLElement, text = ''): void {
    element.classList.add(TRANSITION_CLASSES.loadingShimmer)
    element.textContent = text
  },

  /**
   * Remove loading skeleton effect
   */
  removeLoadingSkeleton(element: HTMLElement): void {
    element.classList.remove(TRANSITION_CLASSES.loadingShimmer)
  },

  /**
   * Create a smooth modal transition
   */
  createModalTransition(modal: HTMLElement): void {
    modal.classList.add(TRANSITION_CLASSES.enhancedModal)
  },

  /**
   * Create enhanced button effects
   */
  enhanceButton(button: HTMLElement): void {
    button.classList.add(
      TRANSITION_CLASSES.enhancedButton,
      TRANSITION_CLASSES.interactive,
      TRANSITION_CLASSES.hoverLift
    )
  },

  /**
   * Create enhanced card effects
   */
  enhanceCard(card: HTMLElement): void {
    card.classList.add(
      TRANSITION_CLASSES.enhancedCard,
      TRANSITION_CLASSES.hoverLift,
      TRANSITION_CLASSES.gpuAccelerated
    )
  },

  /**
   * Create enhanced form input effects
   */
  enhanceFormInput(input: HTMLElement): void {
    input.classList.add(
      TRANSITION_CLASSES.enhancedFormInput,
      TRANSITION_CLASSES.focusRing
    )
  },

  /**
   * Create enhanced table row effects
   */
  enhanceTableRow(row: HTMLElement): void {
    row.classList.add(TRANSITION_CLASSES.enhancedTableRow)
  },

  /**
   * Create a smooth error animation
   */
  showError(element: HTMLElement): void {
    element.classList.add('error-shake')
    setTimeout(() => {
      element.classList.remove('error-shake')
    }, 500)
  },

  /**
   * Create a smooth success animation
   */
  showSuccess(element: HTMLElement): void {
    element.classList.add('success-bounce')
    setTimeout(() => {
      element.classList.remove('success-bounce')
    }, 600)
  },

  /**
   * Create a smooth notification animation
   */
  showNotification(element: HTMLElement): void {
    element.classList.add('notification-enhanced')
  },

  /**
   * Debounce function for performance optimization
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout
    return (...args: Parameters<T>) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  },

  /**
   * Throttle function for performance optimization
   */
  throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    }
  },

  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  },

  /**
   * Apply transitions only if user doesn't prefer reduced motion
   */
  applyIfMotionAllowed(
    element: HTMLElement,
    classes: string[]
  ): void {
    if (!this.prefersReducedMotion()) {
      this.applyTransitionClasses(element, classes)
    }
  },

  /**
   * Create a smooth dropdown transition
   */
  enhanceDropdown(dropdown: HTMLElement): void {
    dropdown.classList.add('dropdown-enhanced')
  },

  /**
   * Create enhanced tab transitions
   */
  enhanceTabs(tabsContainer: HTMLElement): void {
    tabsContainer.classList.add('tab-enhanced')
  },

  /**
   * Create enhanced navigation transitions
   */
  enhanceNavigation(navItem: HTMLElement): void {
    navItem.classList.add('nav-item-enhanced')
  },

  /**
   * Create enhanced sidebar transitions
   */
  enhanceSidebar(sidebar: HTMLElement): void {
    sidebar.classList.add('sidebar-enhanced')
  },

  /**
   * Create enhanced progress bar transitions
   */
  enhanceProgress(progressBar: HTMLElement): void {
    progressBar.classList.add('progress-enhanced')
  },

  /**
   * Create enhanced tooltip transitions
   */
  enhanceTooltip(tooltip: HTMLElement): void {
    tooltip.classList.add('tooltip-enhanced')
  },

  /**
   * Initialize all enhanced transitions on page load
   */
  initializePageTransitions(): void {
    // Enhance all buttons
    document.querySelectorAll('button, .ant-btn').forEach((button) => {
      this.enhanceButton(button as HTMLElement)
    })

    // Enhance all cards
    document.querySelectorAll('.ant-card, .card-border').forEach((card) => {
      this.enhanceCard(card as HTMLElement)
    })

    // Enhance all form inputs
    document.querySelectorAll('input, textarea, select').forEach((input) => {
      this.enhanceFormInput(input as HTMLElement)
    })

    // Enhance all table rows
    document.querySelectorAll('tbody tr').forEach((row) => {
      this.enhanceTableRow(row as HTMLElement)
    })

    // Enhance all modals
    document.querySelectorAll('.ant-modal').forEach((modal) => {
      this.createModalTransition(modal as HTMLElement)
    })

    // Enhance all dropdowns
    document.querySelectorAll('.ant-select-dropdown').forEach((dropdown) => {
      this.enhanceDropdown(dropdown as HTMLElement)
    })

    // Enhance all tabs
    document.querySelectorAll('.ant-tabs').forEach((tabs) => {
      this.enhanceTabs(tabs as HTMLElement)
    })

    // Enhance all navigation items
    document.querySelectorAll('.ant-menu-item, .nav-item').forEach((navItem) => {
      this.enhanceNavigation(navItem as HTMLElement)
    })

    // Enhance all sidebars
    document.querySelectorAll('.ant-layout-sider, .sidebar').forEach((sidebar) => {
      this.enhanceSidebar(sidebar as HTMLElement)
    })

    // Enhance all progress bars
    document.querySelectorAll('.ant-progress').forEach((progress) => {
      this.enhanceProgress(progress as HTMLElement)
    })

    // Enhance all tooltips
    document.querySelectorAll('.ant-tooltip').forEach((tooltip) => {
      this.enhanceTooltip(tooltip as HTMLElement)
    })

    // Initialize scroll reveal for elements with scroll-reveal class
    const scrollRevealElements = document.querySelectorAll('.scroll-reveal')
    if (scrollRevealElements.length > 0) {
      this.createScrollReveal(scrollRevealElements)
    }
  },

  /**
   * Clean up transitions (useful for component unmounting)
   */
  cleanupTransitions(element: HTMLElement): void {
    const allTransitionClasses = Object.values(TRANSITION_CLASSES)
    element.classList.remove(...allTransitionClasses)
  }
}

// Export default instance
export default TransitionUtils

// Hook for React components
export const useTransitions = (): any => {
  return {
    applyTransitionClasses: TransitionUtils.applyTransitionClasses,
    removeTransitionClasses: TransitionUtils.removeTransitionClasses,
    applyStaggerAnimation: TransitionUtils.applyStaggerAnimation,
    createScrollReveal: TransitionUtils.createScrollReveal,
    createPageTransition: TransitionUtils.createPageTransition,
    createLoadingSkeleton: TransitionUtils.createLoadingSkeleton,
    removeLoadingSkeleton: TransitionUtils.removeLoadingSkeleton,
    enhanceButton: TransitionUtils.enhanceButton,
    enhanceCard: TransitionUtils.enhanceCard,
    enhanceFormInput: TransitionUtils.enhanceFormInput,
    enhanceTableRow: TransitionUtils.enhanceTableRow,
    showError: TransitionUtils.showError,
    showSuccess: TransitionUtils.showSuccess,
    showNotification: TransitionUtils.showNotification,
    debounce: TransitionUtils.debounce,
    throttle: TransitionUtils.throttle,
    prefersReducedMotion: TransitionUtils.prefersReducedMotion,
    applyIfMotionAllowed: TransitionUtils.applyIfMotionAllowed,
    cleanupTransitions: TransitionUtils.cleanupTransitions
  }
}
